// Step 1: Client Profile

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import type { WizardStepProps } from '@/lib/onboarding/types';
import { supabase } from '@/integrations/supabase/client';
import { useClientOnboarding } from '@/hooks/useClientOnboarding';
import { toast } from 'sonner';

const US_STATES = [
  'OH', 'PA', 'MI', 'IN', 'KY', 'WV', 'NY', 'CA', 'TX', 'FL', 
  'IL', 'GA', 'NC', 'NJ', 'VA', 'WA', 'AZ', 'MA', 'TN', 'MO'
];

const formSchema = z.object({
  client_name: z.string().min(2, 'Client name must be at least 2 characters'),
  client_type: z.enum(['HOSPITAL', 'SURGICAL_PRACTICE']),
  primary_state: z.string().min(2, 'Please select a state'),
  primary_contact_name: z.string().min(2, 'Contact name is required'),
  primary_contact_email: z.string().email('Invalid email address'),
  go_live_target_date: z.date({ required_error: 'Go-live date is required' }),
});

type FormValues = z.infer<typeof formSchema>;

export function Step1ClientProfile({ clientId, progress, onComplete }: WizardStepProps) {
  const { completeStep, isCompleting } = useClientOnboarding(clientId);
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      client_name: '',
      client_type: 'HOSPITAL',
      primary_state: '',
      primary_contact_name: progress.primary_contact_name || '',
      primary_contact_email: progress.primary_contact_email || '',
      go_live_target_date: progress.go_live_target_date 
        ? new Date(progress.go_live_target_date) 
        : undefined,
    },
  });

  // Load client data
  useEffect(() => {
    async function loadClient() {
      const { data } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();

      if (data) {
        form.setValue('client_name', data.client_name);
        form.setValue('client_type', data.client_type as 'HOSPITAL' | 'SURGICAL_PRACTICE');
        form.setValue('primary_state', data.primary_state || '');
      }
      setIsLoadingClient(false);
    }
    loadClient();
  }, [clientId, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      // Update client record
      await supabase
        .from('clients')
        .update({
          client_name: values.client_name,
          client_type: values.client_type,
          primary_state: values.primary_state,
        })
        .eq('id', clientId);

      // Complete step with contact info
      completeStep({
        step: 1,
        stepData: {
          primary_contact_name: values.primary_contact_name,
          primary_contact_email: values.primary_contact_email,
          go_live_target_date: format(values.go_live_target_date, 'yyyy-MM-dd'),
        },
      }, {
        onSuccess: onComplete,
      });
    } catch (error) {
      toast.error('Failed to save client profile');
    }
  };

  if (isLoadingClient) {
    return (
      <Card>
        <CardContent className="py-10">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Client Profile</CardTitle>
        <CardDescription>
          Enter the basic information for this client organization.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="client_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Cincinnati Orthopedic Associates" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="client_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="HOSPITAL">Hospital</SelectItem>
                        <SelectItem value="SURGICAL_PRACTICE">Surgical Practice</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="primary_state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary State *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="go_live_target_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Go-Live Target Date *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-t pt-6">
              <h4 className="text-sm font-medium mb-4">Primary Contact</h4>
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="primary_contact_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane Smith" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="primary_contact_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Email *</FormLabel>
                      <FormControl>
                        <Input placeholder="jane.smith@hospital.org" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={isCompleting}>
                {isCompleting ? 'Saving...' : 'Continue to Payer Selection'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
